import { useEffect, useRef, useState } from "react";
import { useEscapeKey } from "@hooks/useEscapeKey";
import useDialogStore from "@store/useDialogStore";

import { IMeeting, IMeetingObject } from "@interfaces/meeting.interface";

import { BaseDialog } from "@ui/Dialogs/BaseDialog";
import { SectionHeading } from "@ui/Headings";
import { Checkbox } from "@ui/FormElements";
import { Paragraph } from "@ui/Content";
import { View } from "@ui/View";
import { Counter } from "@ui/Counter";

import { SuggestionItem } from "@components/TaggingComponent/SuggestionItems/SuggestionItem";
import { objectTypeToGroup, meetingObjectToTag } from "@utils/tags.util";

interface Props {
  meeting: IMeeting;
  meetingObjects: IMeetingObject[];
  onSubmit: (discussedTopicIds: string[]) => void;
}

export function RemoveMeetingDialog({ meeting, meetingObjects, onSubmit }: Props) {
  const { isOpen, closeDialog } = useDialogStore();
  const [meetingObjectIds, setMeetingObjectIds] = useState<string[]>([]);
  const firstInputRef = useRef(null);

  const showObjects = meetingObjects?.length > 0;
  const title = showObjects ? "Delete meeting?" : "Delete this meeting and its notes?";

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;

    const newIds = checked
      ? [...meetingObjectIds, id]
      : meetingObjectIds.filter((existingId) => existingId !== id);
    setMeetingObjectIds(newIds);
  };

  const handleSubmit = () => onSubmit(meetingObjectIds);

  const handleDialogClose = () => closeDialog();

  useEffect(() => {
    // set all ids to check all checkboxes automatically after initialization
    if (showObjects) {
      const initialIds = meetingObjects.filter(Boolean).map((meetingObject) => meetingObject.id);
      setMeetingObjectIds(initialIds);
    }
  }, [meetingObjects]);

  useEscapeKey({ callback: closeDialog, enabled: isOpen });

  if (!meeting || !meetingObjects) return null;

  return (
    <BaseDialog
      title={title}
      showCloseButton={false}
      key={meeting.id}
      initialFocus={firstInputRef}
      primaryButton={{
        label: "Delete Meeting",
        callback: handleSubmit,
      }}
      secondaryButton={{
        label: "Cancel",
        buttonTypeOverride: "CANCEL",
        callback: handleDialogClose,
      }}
      titleClassName={"text-base"}
      position={showObjects ? "TOP" : "CENTER"}
    >
      <div className="w-full space-y-4 px-4">
        <View.Condition if={showObjects}>
          <div className="w-full flex flex-col gap-4">
            <Paragraph className="text-left">
              The following objects were created during the meeting.{" "}
            </Paragraph>
            <Paragraph className="text-left">Select the ones you want to keep.</Paragraph>

            <div className="flex items-center gap-1">
              <SectionHeading>Keep in Meridian</SectionHeading>
              <Counter value={meetingObjectIds.length} />
            </div>

            <div className="flex flex-col gap-2">
              {meetingObjects.filter(Boolean).map((meetingObject) => (
                <Checkbox
                  label={
                    <SuggestionItem
                      item={meetingObjectToTag(meetingObject)}
                      group={objectTypeToGroup(meetingObject.object_type)}
                      style={{ padding: 0 }}
                    />
                  }
                  labelClassName="overflow-hidden text-ellipsis whitespace-nowrap"
                  id={meetingObject.id}
                  key={meetingObject.id}
                  isChecked={meetingObjectIds.includes(meetingObject.id)}
                  onChange={handleCheckboxChange}
                />
              ))}
            </div>
          </div>
        </View.Condition>
      </div>
    </BaseDialog>
  );
}
